/**
 * How an app package reports a path it does not own.
 *
 * An app mounts under a splat route and resolves its own subtree, so a path it
 * does not recognise never reaches the host router's matcher — `useRoutes`
 * returns null and the user gets a blank screen where the shell's 404 used to
 * be. The app has to hand the miss back somehow, and there are two ways:
 *
 *   1. `AppNotFound` — the host provides the element to render, the app renders
 *      it. Preferred: nothing is thrown, so nothing lands in the console.
 *   2. `notFound()` — throw a route error response and let the host's
 *      errorElement catch it. The fallback, for a host that provides nothing.
 *
 * (1) exists because (2) is observably not free. React surfaces an error its
 * boundary caught to `window.onerror` as well, so every in-app 404 wrote an
 * uncaught `pageerror` into the console — enough to fail ui-cli's console UX
 * gate on a merely mistyped URL, and enough to bill a user's typo as an
 * exception in any error reporter. Before the route split an unmatched path was
 * a clean matcher miss that threw nothing, so (2) was a regression in
 * observability even though what rendered looked identical.
 *
 * The app never learns which page it gets. It asks for "not found" and the host
 * decides, which is what keeps the dependency pointing one way.
 */ function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext } from 'react';
/**
 * React Router identifies route error responses structurally
 * (`isRouteErrorResponse` checks status/statusText/internal/data) and exports
 * no constructor for one. Hence the hand-built shape — but built on Error, not
 * a bare object literal: webpack-dev-server's overlay formats a thrown
 * non-Error as "[object Object]" and covers the very page this is meant to
 * show. It lives here rather than in each app package so there is a single
 * place to fix if that contract changes.
 */ export class RouteNotFoundError extends Error {
    constructor(message){
        super(message), _define_property(this, "status", 404), _define_property(this, "statusText", 'Not Found'), _define_property(this, "internal", false), _define_property(this, "data", void 0);
        this.name = 'RouteNotFoundError';
        this.data = message;
    }
}
export function notFound(message) {
    throw new RouteNotFoundError(message);
}
/**
 * The host's not-found element, supplied at the mount point. Null when no host
 * provided one, which is the signal to fall back to throwing.
 */ const NotFoundRenderContext = /*#__PURE__*/ createContext(null);
/**
 * Host-side: wrap an app mount so a miss inside it renders the shell's own
 * not-found page instead of throwing.
 *
 * `render` is a function rather than an element so the page is only constructed
 * when a miss actually happens.
 */ export function NotFoundProvider({ render, children }) {
    return /*#__PURE__*/ _jsx(NotFoundRenderContext.Provider, {
        value: render,
        children: children
    });
}
/**
 * App-side: the element for an app's catch-all route.
 *
 * Renders whatever the host provided. With no provider it throws instead, so an
 * unconfigured host still gets its errorElement 404 rather than a blank screen
 * — the old behaviour, kept as the floor rather than the norm.
 *
 * `reason` is developer-facing: it becomes the thrown error's message on the
 * fallback path and is never shown to a user on the rendered path.
 */ export function AppNotFound({ reason }) {
    const render = useContext(NotFoundRenderContext);
    if (!render) notFound(reason);
    return render();
}
